export const TENANT_PROFILE_FIELDS = [
  "firstName",
  "middleName",
  "lastName",
  "email",
  "businessName",
  "streetAddress",
  "city",
  "state",
  "zipCode",
  "mobilePhone",
  "businessPhone",
  "homePhone",
  "buildingAccessKeyNumber",
];

export const TENANT_FIELD_LABELS = {
  firstName: "First name",
  middleName: "Middle name",
  lastName: "Last name",
  email: "Email",
  businessName: "Legal business name",
  streetAddress: "Street address",
  city: "City",
  state: "State",
  zipCode: "ZIP code",
  mobilePhone: "Mobile phone",
  businessPhone: "Business phone",
  homePhone: "Home phone",
  buildingAccessKeyNumber: "Building access key number",
  mailboxIds: "Mailbox assignment",
};

export const US_STATES = [
  ["AL", "Alabama"], ["AK", "Alaska"], ["AZ", "Arizona"], ["AR", "Arkansas"],
  ["CA", "California"], ["CO", "Colorado"], ["CT", "Connecticut"], ["DE", "Delaware"],
  ["FL", "Florida"], ["GA", "Georgia"], ["HI", "Hawaii"], ["ID", "Idaho"],
  ["IL", "Illinois"], ["IN", "Indiana"], ["IA", "Iowa"], ["KS", "Kansas"],
  ["KY", "Kentucky"], ["LA", "Louisiana"], ["ME", "Maine"], ["MD", "Maryland"],
  ["MA", "Massachusetts"], ["MI", "Michigan"], ["MN", "Minnesota"], ["MS", "Mississippi"],
  ["MO", "Missouri"], ["MT", "Montana"], ["NE", "Nebraska"], ["NV", "Nevada"],
  ["NH", "New Hampshire"], ["NJ", "New Jersey"], ["NM", "New Mexico"], ["NY", "New York"],
  ["NC", "North Carolina"], ["ND", "North Dakota"], ["OH", "Ohio"], ["OK", "Oklahoma"],
  ["OR", "Oregon"], ["PA", "Pennsylvania"], ["RI", "Rhode Island"], ["SC", "South Carolina"],
  ["SD", "South Dakota"], ["TN", "Tennessee"], ["TX", "Texas"], ["UT", "Utah"],
  ["VT", "Vermont"], ["VA", "Virginia"], ["WA", "Washington"], ["WV", "West Virginia"],
  ["WI", "Wisconsin"], ["WY", "Wyoming"], ["DC", "District of Columbia"],
];

const STATE_CODES = new Set(US_STATES.map(([code]) => code));
const PHONE_FIELDS = ["mobilePhone", "businessPhone", "homePhone"];
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ZIP_PATTERN = /^\d{5}(?:-\d{4})?$/;
const NORMALIZED_PHONE_PATTERN = /^\(\d{3}\) \d{3}-\d{4}$/;

function optionalString(value) {
  if (value === undefined || value === null) return null;
  const normalized = String(value).trim();
  return normalized || null;
}

export function normalizePhone(value) {
  const normalized = optionalString(value);
  if (!normalized) return null;

  let digits = normalized.replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) digits = digits.slice(1);
  if (digits.length !== 10) return normalized;

  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

export function normalizeTenantProfile(profile = {}) {
  return {
    firstName: optionalString(profile.firstName),
    middleName: optionalString(profile.middleName),
    lastName: optionalString(profile.lastName),
    email: optionalString(profile.email)?.toLowerCase() || null,
    businessName: optionalString(profile.businessName),
    streetAddress: optionalString(profile.streetAddress),
    city: optionalString(profile.city),
    state: optionalString(profile.state)?.toUpperCase() || null,
    zipCode: optionalString(profile.zipCode),
    mobilePhone: normalizePhone(profile.mobilePhone),
    businessPhone: normalizePhone(profile.businessPhone),
    homePhone: normalizePhone(profile.homePhone),
    buildingAccessKeyNumber: optionalString(profile.buildingAccessKeyNumber),
  };
}

export function tenantProfileFormValues(profile = {}) {
  const source = profile || {};
  return Object.fromEntries(
    TENANT_PROFILE_FIELDS.map((field) => [field, source[field] || ""]),
  );
}

export function validateTenantProfile(profile) {
  const errors = {};
  if (!profile.firstName) errors.firstName = "First name is required";
  if (!profile.lastName) errors.lastName = "Last name is required";
  if (!profile.email) errors.email = "Email is required";
  else if (!EMAIL_PATTERN.test(profile.email)) errors.email = "Enter a valid email address";
  if (!profile.streetAddress) errors.streetAddress = "Street address is required";
  if (!profile.city) errors.city = "City is required";
  if (!profile.state) errors.state = "State is required";
  else if (!STATE_CODES.has(profile.state)) errors.state = "Select a valid US state";
  if (!profile.zipCode) errors.zipCode = "ZIP code is required";
  else if (!ZIP_PATTERN.test(profile.zipCode)) errors.zipCode = "Use a 5-digit ZIP or ZIP+4";

  if (!PHONE_FIELDS.some((field) => profile[field])) {
    errors.phones = "Enter at least one phone number";
  }
  PHONE_FIELDS.forEach((field) => {
    if (profile[field] && !NORMALIZED_PHONE_PATTERN.test(profile[field])) {
      errors[field] = "Enter a valid 10-digit US phone number";
    }
  });
  return errors;
}

export function tenantDisplayName(tenant = {}) {
  const firstName = optionalString(tenant.firstName);
  const lastName = optionalString(tenant.lastName);
  if (firstName && lastName && firstName === lastName) return firstName;
  return [firstName, lastName].filter(Boolean).join(" ");
}

export function changedTenantProfileFields(original, updated) {
  const before = normalizeTenantProfile(original);
  const after = normalizeTenantProfile(updated);
  return TENANT_PROFILE_FIELDS.filter((field) => before[field] !== after[field]);
}

export function preferredTenantPhone(tenant) {
  if (tenant.mobilePhone) return { number: tenant.mobilePhone, type: "Mobile" };
  if (tenant.businessPhone) return { number: tenant.businessPhone, type: "Business" };
  if (tenant.homePhone) return { number: tenant.homePhone, type: "Home" };
  return null;
}
